import { Component, EventEmitter, inject, OnInit } from '@angular/core';
import { fadeInAnimation } from '../../../animations';
import { SetSelectedCustomerAction } from '../../../core/store/session/selected-customer/select-customer.actions';
import { CustomersClearAction, CustomersLoadAction } from '../../../core/store/session/user-context/user-context.actions';
import { BaseContainer } from '../../base/BaseContainer';
import { Tile, TilesGroupedByLetter } from '../tile/tile.interface';
import { filter, map, switchMap } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { Definition } from '../../../core/store/definitions/definition.interface';
import { SelectCustomerSandbox } from './select-customer.sandbox';

const SALES_ORGANISATIONS_TAB = 0;
const CUSTOMERS_TAB = 1;

@Component({
  selector: 'app-select-customer-container',
  templateUrl: './select-customer.container.html',
  styleUrls: ['./select-customer.container.scss'],
  animations: [fadeInAnimation],
})
export class SelectCustomerContainer extends BaseContainer implements OnInit {
  sandbox = inject(SelectCustomerSandbox);
  clearFilter = new EventEmitter<boolean>();

  selectedTab$ = this.sandbox.customers$.pipe(
    map(customers => customers?.length > 0 ? CUSTOMERS_TAB : SALES_ORGANISATIONS_TAB)
  );
  customersTabDisabled$ = this.selectedTab$.pipe(
    map(selectedTab => selectedTab === SALES_ORGANISATIONS_TAB)
  );

  ngOnInit() {
    this.selectSalesOrganisationIfOnly1Available();
    this.selectCustomerIfOnly1Available();
    this.clearCustomersListIfEpsUserAndNoSalesOrganisationSelected();
    this.setActiveCustomerIfCustomerSelected();
  }

  private setActiveCustomerIfCustomerSelected() {
    combineLatest([this.sandbox.selectedCustomerId$, this.sandbox.customers$]).pipe(
      filter(([selectedCustomerId, customers]) => selectedCustomerId && customers?.length > 0),
      this.takeUntilDestroyed(),
    ).subscribe(([selectedCustomerId, customers]) => {
      const customer = customers.find(customer => customer.id === selectedCustomerId);
      if (customer) {
        this.store.dispatch(new SetSelectedCustomerAction({ customer }));
      }
    });
  }

  private clearCustomersListIfEpsUserAndNoSalesOrganisationSelected() {
    this.sandbox.isEpsUser$.pipe(
      filter(isEpsUser => isEpsUser),
      switchMap(() => this.sandbox.selectedSalesOrganisationId$)
    ).subscribe(selectedSalesOrganisationId => {
      if (selectedSalesOrganisationId) {
        this.store.dispatch(new CustomersLoadAction(selectedSalesOrganisationId));
      } else {
        this.store.dispatch(new CustomersClearAction());
      }
    });
  }

  private selectCustomerIfOnly1Available() {
    this.sandbox.customers$.subscribe(customers => {
      if (customers?.length === 1) {
        this.sandbox.selectedCustomerIdSubject$.next(customers[0].id);
      }
    });
  }

  private selectSalesOrganisationIfOnly1Available() {
    this.sandbox.salesOrganisations$.pipe(
      filter(salesOrganisations => salesOrganisations?.length === 1),
      map(salesOrganisations => salesOrganisations[0])
    ).subscribe(salesOrganisation => {
      this.handleSalesOrganisationClicked(salesOrganisation);
    });
  }

  private mapToTile(tiles?: Definition[], copyIdToNumber = false): Tile[] {
    if (!tiles) {
      return [];
    }
    return tiles.map(tile => ({...tile, number: copyIdToNumber ? tile.id : undefined, name: tile.name || ''}));
  }

  private groupByLetter(list: Tile[]): TilesGroupedByLetter[] {
    if (!list) {
      return [];
    }

    // sort first
    list.sort((a, b) => {
      const nameASplit = a.name.split('] ')[1];
      const nameBSplit = b.name.split('] ')[1];
      const nameA = nameASplit && nameASplit.length && nameASplit.length === 2 ? nameASplit[1] : a.name;
      const nameB = nameBSplit && nameBSplit.length && nameBSplit.length === 2 ? nameBSplit[1] : b.name;

      return nameA.localeCompare(nameB);
    });

    return list.reduce((res, c) => {
      const nameSplit = c.name.split('] ');
      const name = nameSplit && nameSplit.length && nameSplit.length === 2 ? nameSplit[1] : c.name;
      const firstChar = name[0].toLocaleUpperCase();
      let entry = res.find(e => e.firstLetter === firstChar);
      if (!entry) {
        entry = { firstLetter: firstChar, tiles: [] };
        res.push(entry);
      }
      entry.tiles.push(c);
      return res;
    }, []);
  }

  handleSalesOrganisationClicked(salesOrganisation) {
    if (salesOrganisation) {
      this.sandbox.selectedSalesOrganisationIdSubject$.next(salesOrganisation.id);
    }
  }

  async handleCustomerClicked(customer) {
    this.sandbox.selectedCustomerIdSubject$.next(customer.id);
  }

  tabChanged(tab) {
    this.clearFilter.emit(true);
    if (tab.index === SALES_ORGANISATIONS_TAB) {
      this.sandbox.selectedSalesOrganisationIdSubject$.next(undefined);
    }
  }
}
